import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Settings, Lock, Edit, LayoutDashboard, Clock, Calendar, Sparkles, 
  Phone, TrendingUp, Image as ImageIcon, Loader2, Timer, CreditCard, HelpCircle
} from "lucide-react";
import { startUniversalBookingTrial, useUniversalBookingAccess } from "@/hooks/useUniversalBookingAccess";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ProviderType } from "@/types/booking";
import { TooltipContextual } from "@/components/ayuda/TooltipContextual";

export type ProfessionalType = ProviderType;

interface ProfileManagementCardProps {
  profileId: string;
  profileSlug?: string;
  isOwner: boolean;
  isPremium: boolean;
  professionalType: ProfessionalType;
  hasBookingAccess?: boolean;
  daysRemaining?: number | null;
  subscriptionTier?: 'annual' | 'lifetime' | null;
  onBookingActivated?: () => void;
}

const PROFESSIONAL_CONFIG: Record<ProfessionalType, {
  editPath: string;
  imagesPath: string;
  dashboardPath: string;
  servicesPath: string;
  availabilityPath: string;
  becomePath: string;
  themeColor: string;
  label: string;
  providerType: ProviderType;
}> = {
  trainer: {
    editPath: '/edit-trainer-profile',
    imagesPath: '/edit-trainer-profile#media',
    dashboardPath: '/dashboard/reservas',
    servicesPath: '/dashboard/servicios',
    availabilityPath: '/dashboard/disponibilidad',
    becomePath: '/become-trainer',
    themeColor: 'primary',
    label: 'Entrenador',
    providerType: 'trainer',
  },
  nutritionist: {
    editPath: '/edit-nutritionist-profile',
    imagesPath: '/edit-nutritionist-profile#media',
    dashboardPath: '/dashboard/nutricionista/reservas',
    servicesPath: '/dashboard/nutricionista/servicios',
    availabilityPath: '/dashboard/nutricionista/disponibilidad',
    becomePath: '/become-professional',
    themeColor: 'emerald',
    label: 'Nutricionista',
    providerType: 'nutritionist',
  },
  physiotherapist: {
    editPath: '/edit-physiotherapist-profile',
    imagesPath: '/edit-physiotherapist-profile#media',
    dashboardPath: '/dashboard/fisioterapeuta/reservas',
    servicesPath: '/dashboard/fisioterapeuta/servicios',
    availabilityPath: '/dashboard/fisioterapeuta/disponibilidad',
    becomePath: '/become-physiotherapist',
    themeColor: 'rose',
    label: 'Fisioterapeuta',
    providerType: 'physiotherapist',
  },
  yoga: {
    editPath: '/dashboard/yoga/perfil',
    imagesPath: '/dashboard/yoga/perfil#media',
    dashboardPath: '/dashboard/yoga/reservas',
    servicesPath: '/dashboard/yoga/servicios',
    availabilityPath: '/dashboard/yoga/disponibilidad',
    becomePath: '/become-yoga',
    themeColor: 'purple',
    label: 'Estudio de Yoga',
    providerType: 'yoga',
  },
  crossfit: {
    editPath: '/dashboard/crossfit/perfil',
    imagesPath: '/dashboard/crossfit/perfil#media',
    dashboardPath: '/dashboard/crossfit/reservas',
    servicesPath: '/dashboard/crossfit/servicios',
    availabilityPath: '/dashboard/crossfit/disponibilidad',
    becomePath: '/become-crossfit',
    themeColor: 'orange',
    label: 'Box de CrossFit',
    providerType: 'crossfit',
  },
  gym: {
    editPath: '/dashboard/gym/perfil',
    imagesPath: '/dashboard/gym/perfil#media',
    dashboardPath: '/dashboard/gym/reservas',
    servicesPath: '/dashboard/gym/servicios',
    availabilityPath: '/dashboard/gym/disponibilidad',
    becomePath: '/become-gym',
    themeColor: 'green',
    label: 'Gimnasio',
    providerType: 'gym',
  },
  pilates: {
    editPath: '/dashboard/pilates/perfil',
    imagesPath: '/dashboard/pilates/perfil#media',
    dashboardPath: '/dashboard/pilates/reservas',
    servicesPath: '/dashboard/pilates/servicios',
    availabilityPath: '/dashboard/pilates/disponibilidad',
    becomePath: '/become-pilates',
    themeColor: 'teal',
    label: 'Centro de Pilates',
    providerType: 'pilates',
  },
  dance: {
    editPath: '/dashboard/dance/perfil',
    imagesPath: '/dashboard/dance/perfil#media',
    dashboardPath: '/dashboard/dance/reservas',
    servicesPath: '/dashboard/dance/servicios',
    availabilityPath: '/dashboard/dance/disponibilidad',
    becomePath: '/become-dance',
    themeColor: 'pink',
    label: 'Academia de Baile',
    providerType: 'dance',
  },
  martial_arts: {
    editPath: '/dashboard/martial-arts/perfil',
    imagesPath: '/dashboard/martial-arts/perfil#media',
    dashboardPath: '/dashboard/martial-arts/reservas',
    servicesPath: '/dashboard/martial-arts/servicios',
    availabilityPath: '/dashboard/martial-arts/disponibilidad',
    becomePath: '/become-martial-arts',
    themeColor: 'red',
    label: 'Artes Marciales',
    providerType: 'martial_arts',
  },
};

export const ProfileManagementCard = ({
  profileId,
  profileSlug,
  isOwner,
  isPremium,
  professionalType,
  hasBookingAccess = false,
  daysRemaining = null,
  subscriptionTier = null,
  onBookingActivated,
}: ProfileManagementCardProps) => {
  const navigate = useNavigate();
  const config = PROFESSIONAL_CONFIG[professionalType];
  const [isActivating, setIsActivating] = useState(false);

  // Only show for profile owners
  if (!isOwner) return null;

  // Determine access state
  const isTrialActive = hasBookingAccess && !subscriptionTier && daysRemaining !== null && daysRemaining > 0;
  const isTrialExpiringSoon = isTrialActive && daysRemaining !== null && daysRemaining <= 30;
  const isSubscribed = subscriptionTier === 'annual' || subscriptionTier === 'lifetime';
  const hasAccess = isPremium || hasBookingAccess || isSubscribed;
  const showTrialCountdown = isTrialActive && !isSubscribed;

  const handleActivateTrial = async () => {
    setIsActivating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Debes iniciar sesión para activar el período de prueba");
        setIsActivating(false);
        return;
      }

      const result = await startUniversalBookingTrial(
        config.providerType,
        profileId,
        user.id
      );

      if (result.success) {
        toast.success("¡Período de prueba de 90 días activado!", {
          description: "Ya puedes configurar tus servicios y disponibilidad."
        });
        onBookingActivated?.();
        navigate(config.servicesPath);
      } else {
        toast.error("Error al activar el período de prueba", {
          description: result.error || "Inténtalo de nuevo más tarde"
        });
      }
    } catch (error) {
      console.error("Error activating trial:", error);
      toast.error("Error inesperado al activar el período de prueba");
    } finally {
      setIsActivating(false);
    }
  };

  const handleSubscribe = () => {
    navigate('/premium-upgrade');
  };

  const formatDaysRemaining = (days: number) => {
    if (days === 1) return "1 día restante";
    return `${days} días restantes`;
  };

  return (
    <Card className={`border-2 overflow-hidden relative ${
      hasAccess 
        ? "border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-background" 
        : "border-muted bg-muted/30"
    }`}>
      {hasAccess && (
        <>
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full -mr-16 -mt-16 blur-xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/15 rounded-full -ml-12 -mb-12 blur-lg" />
        </>
      )}
      
      <CardHeader className="relative pb-2">
        <Badge 
          className={`w-fit mb-2 ${
            hasAccess 
              ? isTrialExpiringSoon 
                ? "bg-amber-500 text-white" 
                : "bg-primary text-primary-foreground"
              : "bg-muted-foreground/20 text-muted-foreground"
          }`}
        >
          {isSubscribed ? (
            <>
              <Sparkles className="w-3 h-3 mr-1" />
              Plan {subscriptionTier === 'lifetime' ? 'De por Vida' : 'Anual'}
            </>
          ) : showTrialCountdown ? (
            <>
              <Timer className="w-3 h-3 mr-1" />
              Prueba Gratuita
            </>
          ) : hasAccess ? (
            <>
              <Settings className="w-3 h-3 mr-1" />
              Panel de Gestión
            </>
          ) : (
            <>
              <Lock className="w-3 h-3 mr-1" />
              Acceso Limitado
            </>
          )}
        </Badge>
        <CardTitle className="text-lg">
          {hasAccess ? "Gestiona tu Perfil" : "Tu Perfil"}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative space-y-3">
        {/* Trial Countdown */}
        {showTrialCountdown && daysRemaining !== null && (
          <div className={`p-3 rounded-lg text-center ${
            isTrialExpiringSoon 
              ? "bg-amber-100 dark:bg-amber-950 border border-amber-300 dark:border-amber-700" 
              : "bg-primary/10 border border-primary/20"
          }`}>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Timer className={`w-5 h-5 ${isTrialExpiringSoon ? "text-amber-600" : "text-primary"}`} />
              <span className={`text-2xl font-bold ${isTrialExpiringSoon ? "text-amber-600" : "text-primary"}`}>
                {daysRemaining}
              </span>
            </div>
            <p className={`text-sm ${isTrialExpiringSoon ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground"}`}>
              {formatDaysRemaining(daysRemaining)}
            </p>
            {isTrialExpiringSoon && (
              <p className="text-xs text-destructive mt-1">
                ¡Tu prueba expira pronto!
              </p>
            )}
          </div>
        )}

        {/* Always show edit button for owners */}
        <div className="grid grid-cols-2 gap-2">
          <Button 
            variant="outline"
            size="sm"
            onClick={() => navigate(config.editPath)}
            className="w-full"
          >
            <Edit className="w-3 h-3 mr-1" />
            Editar Perfil
          </Button>
          <Button 
            variant="outline"
            size="sm"
            onClick={() => navigate(config.imagesPath)}
            className="w-full"
          >
            <ImageIcon className="w-3 h-3 mr-1" />
            Imágenes
          </Button>
        </div>

        {hasAccess ? (
          <>
            <p className="text-sm text-muted-foreground">
              Accede al panel de control para gestionar reservas, servicios y disponibilidad.
            </p>
            
            <div className="grid grid-cols-2 gap-2">
              <Button 
                size="sm"
                onClick={() => navigate(config.dashboardPath)}
                className="w-full"
              >
                <LayoutDashboard className="w-3 h-3 mr-1" />
                Reservas
              </Button>
              <Button 
                variant="secondary"
                size="sm"
                onClick={() => navigate(config.servicesPath)}
                className="w-full"
              >
                <Calendar className="w-3 h-3 mr-1" />
                Servicios
              </Button>
            </div>
            
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => navigate(config.availabilityPath)}
              className="w-full text-xs"
            >
              <Clock className="w-3 h-3 mr-1" />
              Gestionar Disponibilidad
            </Button>

            {/* Subscribe button when trial is expiring soon */}
            {isTrialExpiringSoon && !isSubscribed && (
              <Button 
                className="w-full bg-gradient-primary hover:opacity-90 text-primary-foreground"
                onClick={handleSubscribe}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Suscribirse Ahora
              </Button>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Activa el sistema de reservas para gestionar citas y pagos online.
            </p>
            
            <div className="space-y-1.5 opacity-60">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Calendario inteligente</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>Recordatorios automáticos</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
                <span>Stripe & PayPal integrado</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                className="flex-1" 
                onClick={handleActivateTrial}
                disabled={isActivating}
              >
                {isActivating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {isActivating ? "Activando..." : "Activar 90 Días Gratis"}
              </Button>
              <TooltipContextual
                content={
                  <div className="space-y-2 max-w-xs">
                    <p className="font-medium">¿Qué incluye el sistema de reservas?</p>
                    <ul className="text-sm space-y-1">
                      <li>✓ Calendario inteligente de citas</li>
                      <li>✓ Pagos con Stripe y PayPal</li>
                      <li>✓ Recordatorios automáticos por email</li>
                      <li>✓ Panel de gestión de clientes</li>
                    </ul>
                    <p className="text-xs text-muted-foreground pt-2 border-t">
                      Sin tarjeta de crédito requerida. Cancela cuando quieras.
                    </p>
                  </div>
                }
                side="left"
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
